import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { VdpCardType } from '~/constants/home/VdpCardType';

type GlobalContextType = {
  isReturningUser: boolean;
  vdpCardTypeShown: VdpCardType;
  displayLowerFoldOnNative: boolean;
  isVdpLinksLoaded: boolean;
  refreshSrpLinksTrigger: number;
  setIsVdpLinksLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRefreshSrpCard: () => void;
  setDisplayLowerFoldOnNative: React.Dispatch<React.SetStateAction<boolean>>;
  setVdpCardTypeShown: React.Dispatch<React.SetStateAction<VdpCardType>>;
};

export const defaultGlobalContext: GlobalContextType = {
  isReturningUser: false,
  vdpCardTypeShown: VdpCardType.None,
  displayLowerFoldOnNative: true,
  isVdpLinksLoaded: false,
  refreshSrpLinksTrigger: 0,
  setIsVdpLinksLoaded: () => '',
  triggerRefreshSrpCard: () => {},
  setDisplayLowerFoldOnNative: () => '',
  setVdpCardTypeShown: () => ''
};

export type OwnProps = React.PropsWithChildren<{
  isReturningUser: boolean;
  displayLowerFoldOnNative: boolean;
}>;

const GlobalContext = createContext<GlobalContextType>(defaultGlobalContext);

export function GlobalContextProvider({ children, ...props }: OwnProps) {
  const [refreshSrpLinksTrigger, setRefreshSrpLinksTrigger] = useState(
    defaultGlobalContext.refreshSrpLinksTrigger
  );
  const [vdpCardTypeShown, setVdpCardTypeShown] = useState(
    defaultGlobalContext.vdpCardTypeShown
  );
  const [isVdpLinksLoaded, setIsVdpLinksLoaded] = useState(
    defaultGlobalContext.isVdpLinksLoaded
  );

  const triggerRefreshSrpCard = useCallback(() => {
    setRefreshSrpLinksTrigger(Date.now());
  }, []);

  const [displayLowerFoldOnNative, setDisplayLowerFoldOnNative] = useState(
    props.displayLowerFoldOnNative
  );

  const value = useMemo<GlobalContextType>(
    () => ({
      isReturningUser: props.isReturningUser,
      isVdpLinksLoaded,
      refreshSrpLinksTrigger,
      displayLowerFoldOnNative,
      vdpCardTypeShown,
      setVdpCardTypeShown,
      triggerRefreshSrpCard,
      setDisplayLowerFoldOnNative,
      setIsVdpLinksLoaded
    }),
    [
      props.isReturningUser,
      isVdpLinksLoaded,
      refreshSrpLinksTrigger,
      displayLowerFoldOnNative,
      vdpCardTypeShown,
      triggerRefreshSrpCard
    ]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

GlobalContextProvider.displayName = 'GlobalContextProvider';

export function useGetIsReturnUser() {
  return useContext(GlobalContext).isReturningUser;
}

export default GlobalContext;
